import React, { useState } from 'react';

export default function ContactForm() {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    message: '',
    website_url: '' // Honeypot field
  });

  const [status, setStatus] = useState({ type: null, message: '' });
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus({ type: null, message: '' });

    // Client-side honeypot check
    if (formData.website_url) {
      // Quietly ignore bot submissions
      setStatus({
        type: 'success',
        message: 'Tak for din besked. Jørn vender tilbage til dig hurtigst muligt.'
      });
      return;
    }

    setSubmitting(true);

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: formData.name,
          phone: formData.phone,
          email: formData.email,
          message: formData.message,
          website_url: formData.website_url
        })
      });

      if (response.ok) {
        setStatus({
          type: 'success',
          message: 'Mange tak for din henvendelse! Jørn Rolskov vil kontakte dig snarest muligt.'
        });
        setFormData({
          name: '',
          phone: '',
          email: '',
          message: '',
          website_url: ''
        });
      } else {
        const data = await response.json().catch(() => ({}));
        setStatus({
          type: 'error',
          message: data.message || 'Der opstod en fejl ved afsendelse. Du kan i stedet ringe på 27 11 75 57 eller sende en e-mail til joern@rolskov-bowtech.dk.'
        });
      }
    } catch (err) {
      setStatus({
        type: 'error',
        message: 'Kunne ikke forbinde til serveren. Ring gerne direkte på 27 11 75 57.'
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="bg-white rounded-2xl p-6 sm:p-8 border border-[#228A47]/15 shadow-sm space-y-5">
      <div className="space-y-1 border-b border-[#f4efe6] pb-4">
        <h3 className="text-xl font-bold text-[#062364]">Send en besked</h3>
        <p className="text-xs text-slate-500">
          Udfyld formularen, så vender Jørn henvendelsen tilbage hurtigst muligt.
        </p>
      </div>

      {status.type === 'success' && (
        <div className="p-4 rounded-xl bg-[#e8f5ed] border border-[#228A47]/20 text-[#228A47] text-sm font-semibold flex items-center gap-3">
          <i className="fa-solid fa-circle-check text-lg shrink-0" aria-hidden="true"></i>
          <span>{status.message}</span>
        </div>
      )}

      {status.type === 'error' && (
        <div className="p-4 rounded-xl bg-red-50 border border-red-200 text-red-800 text-sm font-semibold flex items-center gap-3">
          <i className="fa-solid fa-circle-exclamation text-lg shrink-0" aria-hidden="true"></i>
          <span>{status.message}</span>
        </div>
      )}

      {/* Honeypot field - mandatory sr-only + absolute */}
      <div className="sr-only absolute -left-[9999px]" aria-hidden="true">
        <label htmlFor="website_url">Honeypot (do not fill)</label>
        <input
          type="text"
          id="website_url"
          name="website_url"
          tabIndex="-1"
          autoComplete="off"
          value={formData.website_url}
          onChange={handleChange}
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {/* Name */}
        <div className="space-y-1.5">
          <label htmlFor="name" className="block text-xs font-semibold text-[#062364] uppercase tracking-wider">
            Navn <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="name"
            name="name"
            required
            value={formData.name}
            onChange={handleChange}
            placeholder="Dit fulde navn"
            className="w-full px-4 py-3 text-sm rounded-xl border border-[#228A47]/20 bg-[#faf8f5] text-[#062364] focus:outline-none focus:ring-2 focus:ring-[#228A47] focus:bg-white transition-all"
          />
        </div>

        {/* Phone */}
        <div className="space-y-1.5">
          <label htmlFor="phone" className="block text-xs font-semibold text-[#062364] uppercase tracking-wider">
            Telefonnummer <span className="text-red-500">*</span>
          </label>
          <input
            type="tel"
            id="phone"
            name="phone"
            required
            value={formData.phone}
            onChange={handleChange}
            placeholder="27 11 75 57"
            className="w-full px-4 py-3 text-sm rounded-xl border border-[#228A47]/20 bg-[#faf8f5] text-[#062364] focus:outline-none focus:ring-2 focus:ring-[#228A47] focus:bg-white transition-all"
          />
        </div>
      </div>

      {/* Email */}
      <div className="space-y-1.5">
        <label htmlFor="email" className="block text-xs font-semibold text-[#062364] uppercase tracking-wider">
          E-mailadresse <span className="text-red-500">*</span>
        </label>
        <input
          type="email"
          id="email"
          name="email"
          required
          value={formData.email}
          onChange={handleChange}
          placeholder="din@email.dk"
          className="w-full px-4 py-3 text-sm rounded-xl border border-[#228A47]/20 bg-[#faf8f5] text-[#062364] focus:outline-none focus:ring-2 focus:ring-[#228A47] focus:bg-white transition-all"
        />
      </div>

      {/* Message */}
      <div className="space-y-1.5">
        <label htmlFor="message" className="block text-xs font-semibold text-[#062364] uppercase tracking-wider">
          Beskrivelse / Spørgsmål <span className="text-red-500">*</span>
        </label>
        <textarea
          id="message"
          name="message"
          rows="4"
          required
          value={formData.message}
          onChange={handleChange}
          placeholder="Skriv hvad du har på hjerte eller hvornår du ønsker en tid..."
          className="w-full px-4 py-3 text-sm rounded-xl border border-[#228A47]/20 bg-[#faf8f5] text-[#062364] focus:outline-none focus:ring-2 focus:ring-[#228A47] focus:bg-white transition-all"
        ></textarea>
      </div>

      <button
        type="submit"
        disabled={submitting}
        className="w-full py-3.5 px-6 text-sm font-semibold text-white bg-[#228A47] hover:bg-[#1b713a] active:bg-[#155a2d] rounded-xl shadow-sm transition-all duration-200 disabled:opacity-50 flex items-center justify-center gap-2"
      >
        {submitting ? (
          <>
            <i className="fa-regular fa-circle-notch animate-spin" aria-hidden="true"></i>
            <span>Sender besked...</span>
          </>
        ) : (
          <>
            <i className="fa-regular fa-paper-plane" aria-hidden="true"></i>
            <span>Send besked</span>
          </>
        )}
      </button>

      <p className="text-[11px] text-slate-400 text-center">
        Dine oplysninger anvendes udelukkende til at besvare din henvendelse.
      </p>
    </form>
  );
}
